import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Surveillance du retour antenne embarqué dans une iframe.
 *
 * Le lecteur est une page servie par la passerelle média : quand le réseau du
 * poste se reconfigure (Chrome émet alors `ERR_NETWORK_CHANGED` et coupe toutes
 * ses connexions), les segments HLS échouent et la vidéo se fige sans que
 * l'iframe ne signale quoi que ce soit. `onLoad` a déjà eu lieu, `onError` ne
 * se déclenche pas : côté opérateur, l'image reste sur la dernière frame et le
 * seul remède connu était de recharger toute la page.
 *
 * On relance donc le lecteur — et lui seul — dès que la vidéo cesse d'avancer.
 * Le repère est `currentTime` : c'est le seul indicateur qui distingue un flux
 * réellement vivant d'une iframe chargée mais morte.
 */

const CHECK_INTERVAL_MS      = 4_000
const FIRST_FRAME_TIMEOUT_MS = 25_000
const STALL_TIMEOUT_MS       = 12_000
const MAX_STALL_TIMEOUT_MS   = 60_000

export interface StreamWatchdog {
  /** À poser sur l'iframe : sa remise à zéro relance le lecteur. */
  playerKey: number
  iframeRef: React.MutableRefObject<HTMLIFrameElement | null>
  /** Vrai tant que la vidéo avance réellement, pas seulement chargée. */
  live: boolean
  /**
   * Aucun retour à montrer : pas d'image dans le délai d'amorçage, ou
   * `/stream/` répondu par l'application elle-même (accès direct à l'API,
   * sans la passerelle qui route vers le serveur média).
   */
  unavailable: boolean
  /** À poser sur `onLoad` de l'iframe : détecte le fallback sans attendre. */
  onFrameLoad: () => void
  /** Relance manuelle (bouton « Recharger »). */
  reload: () => void
}

/**
 * Sans passerelle, `/stream/*` retombe sur le fallback SPA : l'iframe
 * affiche alors l'application en miniature au lieu du lecteur.
 */
export function isAppFallback(doc: Document | null | undefined): boolean {
  return Boolean(doc?.getElementById('root'))
}

export function useStreamWatchdog(): StreamWatchdog {
  const [playerKey, setPlayerKey] = useState(0)
  const [live, setLive] = useState(false)
  const [unavailable, setUnavailable] = useState(false)
  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  const attemptsRef = useRef(0)
  const liveRef = useRef(false)

  const restart = useCallback((manual: boolean) => {
    // Une relance automatique se compte : deux coupures d'affilée espacent la
    // tentative suivante, pour ne pas marteler la passerelle média quand c'est
    // le réseau du poste qui est durablement absent.
    attemptsRef.current = manual ? 0 : attemptsRef.current + 1
    liveRef.current = false
    setLive(false)
    setPlayerKey(value => value + 1)
  }, [])

  const reload = useCallback(() => restart(true), [restart])

  const onFrameLoad = useCallback(() => {
    try {
      if (isAppFallback(iframeRef.current?.contentDocument)) setUnavailable(true)
    } catch {
      // Lecteur d'une autre origine : illisible, le chien de garde décidera.
    }
  }, [])

  useEffect(() => {
    const mountedAt = Date.now()
    let lastTime = -1
    let lastProgressAt = mountedAt
    let readable = true

    const videoElement = (): HTMLVideoElement | null => {
      try {
        return iframeRef.current?.contentDocument?.querySelector('video') ?? null
      } catch {
        // Lecteur servi depuis une autre origine : `currentTime` est
        // inaccessible, on désarme plutôt que de relancer à l'aveugle.
        readable = false
        return null
      }
    }

    const tick = () => {
      if (!readable) return
      // Onglet en arrière-plan : le navigateur met la vidéo en pause pour
      // économiser la batterie. Ce n'est pas une coupure, on ne compte pas.
      if (document.hidden) { lastProgressAt = Date.now(); return }

      const now = Date.now()
      const video = videoElement()
      if (!readable) return

      if (!video) {
        // L'application à la place du lecteur : relancer ne changerait rien,
        // seul un rechargement manuel (après correction de l'accès) retente.
        if (isAppFallback(iframeRef.current?.contentDocument)) {
          setUnavailable(true)
          readable = false
          return
        }
        if (now - mountedAt > FIRST_FRAME_TIMEOUT_MS) {
          setUnavailable(true)
          restart(false)
        }
        return
      }

      if (video.currentTime !== lastTime) {
        lastTime = video.currentTime
        lastProgressAt = now
        attemptsRef.current = 0
        if (!liveRef.current) { liveRef.current = true; setLive(true); setUnavailable(false) }
        return
      }

      // Figé : une simple reprise suffit souvent (autoplay interrompu par
      // l'économiseur d'énergie), on l'essaie avant de tout relancer.
      if (video.paused || video.ended) void video.play().catch(() => {})

      const budget = Math.min(
        STALL_TIMEOUT_MS * Math.max(1, attemptsRef.current + 1),
        MAX_STALL_TIMEOUT_MS,
      )
      if (now - lastProgressAt > budget) {
        if (liveRef.current) { liveRef.current = false; setLive(false) }
        restart(false)
      }
    }

    const timer = window.setInterval(tick, CHECK_INTERVAL_MS)

    // Retour du réseau ou de l'onglet : inutile d'attendre le prochain budget
    // de silence, c'est le moment où la relance a le plus de chances d'aboutir.
    const onOnline = () => { if (!liveRef.current) restart(false) }
    const onVisible = () => {
      lastProgressAt = Date.now()
      if (document.hidden || liveRef.current) return
      // Pas au premier retour d'onglet : le lecteur a peut-être simplement
      // besoin de finir son amorçage.
      if (Date.now() - mountedAt > STALL_TIMEOUT_MS) restart(false)
    }
    window.addEventListener('online', onOnline)
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      window.clearInterval(timer)
      window.removeEventListener('online', onOnline)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [playerKey, restart])

  return { playerKey, iframeRef, live, unavailable, onFrameLoad, reload }
}
